import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, Calendar, Percent, DollarSign, Save, Check, ArrowLeft, Eye, Lock, User, KeyRound,
  Users, CreditCard, BarChart3, Globe, Link2, Copy, RefreshCw, Smartphone, Monitor, Tablet,
  TrendingUp, Clock, MapPin, Activity, ExternalLink, ChevronRight, X, Plus, Trash2, Edit3, LinkIcon,
  Tag, ShoppingCart, RotateCcw, Filter, MousePointerClick
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin credentials
const ADMIN_CREDENTIALS = {
  login: 'vadjik0411',
  password: 'Vfhfhfilhso0411'
};

// Tabs
const TABS = [
  { id: 'dashboard', name: 'Дашборд', icon: BarChart3 },
  { id: 'transactions', name: 'Транзакции', icon: CreditCard },
  { id: 'visitors', name: 'Посетители', icon: Users },
  { id: 'check-lead', name: 'Лиды', icon: Filter },
  { id: 'catalog-prices', name: 'Цены каталога', icon: Tag },
  { id: 'shortlinks', name: 'Короткие ссылки', icon: LinkIcon },
  { id: 'settings', name: 'Настройки', icon: Settings },
  { id: 'utm', name: 'UTM ссылки', icon: Link2 },
];

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ login: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data states
  const [transactions, setTransactions] = useState([]);
  const [transactionStats, setTransactionStats] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);
  const [settings, setSettings] = useState({
    flow_date: '',
    flow_time: '19:00',
    discount_enabled: false,
    discount_type: 'percent',
    discount_value: 0,
    flow_discount_enabled: false,
    flow_discount_type: 'percent',
    flow_discount_value: 0,
    base_discount_enabled: false,
    base_discount_type: 'percent',
    base_discount_value: 0,
    advanced_discount_enabled: false,
    advanced_discount_type: 'percent',
    advanced_discount_value: 0,
    pro_discount_enabled: false,
    pro_discount_type: 'percent',
    pro_discount_value: 0,
    prices: {}
  });
  const [utmLinks, setUtmLinks] = useState([]);
  const [shortLinks, setShortLinks] = useState([]);
  const [catalogPrices, setCatalogPrices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(null);

  // Check if already authenticated
  useEffect(() => {
    const authToken = sessionStorage.getItem('adminAuth');
    if (authToken === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data when authenticated
  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    
    try {
      const [txRes, txStatsRes, visitorsRes, visitorStatsRes, settingsRes, utmRes, shortLinksRes, pricesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/transactions`),
        fetch(`${API_URL}/api/admin/transactions/stats`),
        fetch(`${API_URL}/api/admin/visitors?limit=50`),
        fetch(`${API_URL}/api/admin/visitors/stats`),
        fetch(`${API_URL}/api/admin/settings`),
        fetch(`${API_URL}/api/admin/utm-links`),
        fetch(`${API_URL}/api/admin/shortlinks`),
        fetch(`${API_URL}/api/admin/catalog/prices`),
      ]);
      
      if (txRes.ok) setTransactions(await txRes.json());
      if (txStatsRes.ok) setTransactionStats(await txStatsRes.json());
      if (visitorsRes.ok) setVisitors(await visitorsRes.json());
      if (visitorStatsRes.ok) setVisitorStats(await visitorStatsRes.json());
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
      if (utmRes.ok) setUtmLinks(await utmRes.json());
      if (shortLinksRes.ok) setShortLinks(await shortLinksRes.json());
      if (pricesRes.ok) setCatalogPrices(await pricesRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);
  
  // Handle login
  const handleLogin = (e) => {
    e.preventDefault();
    if (loginData.login === ADMIN_CREDENTIALS.login && loginData.password === ADMIN_CREDENTIALS.password) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'authenticated');
      setLoginError('');
    } else {
      setLoginError('Неверный логин или пароль');
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
  };

  // Save settings handlers
  const handleSaveFlowDate = async () => {
    try {
      await fetch(`${API_URL}/api/admin/settings/flow-date`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_date: settings?.flow_date || '',
          flow_time: settings?.flow_time || '19:00',
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving flow date:', error);
    }
  };

  // NEW: Save per-tier discount (base / advanced / pro) — tier='flow' also works
  const handleSaveTierDiscount = async (tier) => {
    try {
      await fetch(`${API_URL}/api/admin/settings/tier-discount`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier,
          enabled: !!settings?.[`${tier}_discount_enabled`],
          type: settings?.[`${tier}_discount_type`] || 'percent',
          value: Number(settings?.[`${tier}_discount_value`] || 0),
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error(`Error saving ${tier} discount:`, error);
    }
  };

  // NEW: Reset all visitor promo sessions (everyone gets fresh 24h on next visit)
  const handleResetPromoSessions = async () => {
    if (!window.confirm('Сбросить таймеры скидок у всех посетителей? Они получат новые 24 часа при следующем заходе.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/promo/reset`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        alert(`Сброшено сессий: ${data.deleted}`);
      }
    } catch (error) {
      console.error('Error resetting promo sessions:', error);
    }
  };

  const handleSavePrice = async (packageId, price) => {
    try {
      await fetch(`${API_URL}/api/admin/settings/prices`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, price: parseFloat(price) }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving price:', error);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050506] flex items-center justify-center py-12 px-4">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-violet-500/[0.03] rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-amber-500/[0.03] rounded-full blur-[120px]" />
        </div>
        
        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-4 shadow-xl shadow-amber-500/30">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-black text-white">Админ-панель</h1>
            <p className="text-zinc-500 text-sm mt-1">Введите данные для входа</p>
          </div>
          
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-violet-500/20 to-amber-500/20 rounded-3xl blur-xl" />
            
            <form onSubmit={handleLogin} className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-2xl p-8 border border-zinc-800">
              {loginError && (
                <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                  <p className="text-red-400 text-sm text-center">{loginError}</p>
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-zinc-400 text-sm mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Логин
                </label>
                <input
                  type="text"
                  value={loginData.login}
                  onChange={(e) => setLoginData(prev => ({ ...prev, login: e.target.value }))}
                  placeholder="Введите логин"
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-amber-500 focus:outline-none transition-colors"
                  autoComplete="username"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-zinc-400 text-sm mb-2 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" />
                  Пароль
                </label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Введите пароль"
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-amber-500 focus:outline-none transition-colors"
                  autoComplete="current-password"
                />
              </div>
              
              <button
                type="submit"
                className="w-full py-4 px-6 rounded-xl font-bold text-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black hover:shadow-lg hover:shadow-amber-500/30 hover:-translate-y-0.5 transition-all"
              >
                Войти
              </button>
              
              <div className="mt-6 text-center">
                <a href="/" className="text-zinc-500 hover:text-zinc-300 text-sm transition-colors">
                  ← Вернуться на сайт
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main Admin Panel
  return (
    <div className="min-h-screen bg-[#050506]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-zinc-900/80 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="w-10 h-10 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </a>
              <div>
                <h1 className="text-xl font-black text-white flex items-center gap-2">
                  <Settings className="w-6 h-6 text-amber-400" />
                  Админ-панель
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={fetchData}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Обновить
              </button>
              
              <a href="/faster_discount" target="_blank" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors text-sm">
                <Eye className="w-4 h-4" />
                Превью
              </a>
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors text-sm"
              >
                <Lock className="w-4 h-4" />
                Выйти
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Currently Online */}
              <div className="p-6 rounded-2xl bg-gradient-to-b from-emerald-500/10 to-emerald-900/10 border border-emerald-500/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-zinc-400 text-sm">Сейчас на сайте</span>
                </div>
                <p className="text-3xl font-black text-emerald-400">{visitorStats?.currently_active || 0}</p>
              </div>
              
              {/* Today Visitors */}
              <div className="p-6 rounded-2xl bg-gradient-to-b from-blue-500/10 to-blue-900/10 border border-blue-500/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-zinc-400 text-sm">Сегодня</span>
                </div>
                <p className="text-3xl font-black text-blue-400">{visitorStats?.today_visitors || 0}</p>
              </div>
              
              {/* Week Visitors */}
              <div className="p-6 rounded-2xl bg-gradient-to-b from-violet-500/10 to-violet-900/10 border border-violet-500/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-violet-400" />
                  </div>
                  <span className="text-zinc-400 text-sm">За 7 дней</span>
                </div>
                <p className="text-3xl font-black text-violet-400">{visitorStats?.week_visitors || 0}</p>
              </div>
              
              {/* Total Revenue */}
              <div className="p-6 rounded-2xl bg-gradient-to-b from-amber-500/10 to-amber-900/10 border border-amber-500/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-amber-400" />
                  </div>
                  <span className="text-zinc-400 text-sm">Доход</span>
                </div>
                <p className="text-3xl font-black text-amber-400">${transactionStats?.total_revenue || 0}</p>
              </div>
            </div>

            {/* Second Row Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <p className="text-zinc-500 text-xs mb-1">Остались &gt;10 сек (сегодня)</p>
                <p className="text-xl font-bold text-white">{visitorStats?.engaged_today || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <p className="text-zinc-500 text-xs mb-1">Оплат сегодня</p>
                <p className="text-xl font-bold text-emerald-400">{transactionStats?.today_paid || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <p className="text-zinc-500 text-xs mb-1">Оплат за неделю</p>
                <p className="text-xl font-bold text-white">{transactionStats?.week_paid || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <p className="text-zinc-500 text-xs mb-1">Ожидают оплаты</p>
                <p className="text-xl font-bold text-amber-400">{transactionStats?.pending || 0}</p>
              </div>
            </div>

            {/* Traffic Sources & Devices */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Traffic Sources */}
              <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-400" />
                  Источники трафика (7 дней)
                </h3>
                <div className="space-y-3">
                  {visitorStats?.traffic_sources?.map((source, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-zinc-800/50">
                      <span className="text-zinc-300 capitalize">{source._id || 'Прямой'}</span>
                      <span className="text-white font-bold">{source.count}</span>
                    </div>
                  )) || <p className="text-zinc-500 text-sm">Нет данных</p>}
                </div>
              </div>
              
              {/* Devices */}
              <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-violet-400" />
                  Устройства (7 дней)
                </h3>
                <div className="space-y-3">
                  {visitorStats?.device_types?.map((device, i) => {
                    const Icon = device._id === 'mobile' ? Smartphone : device._id === 'tablet' ? Tablet : Monitor;
                    return (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-zinc-800/50">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-zinc-400" />
                          <span className="text-zinc-300 capitalize">{device._id || 'Unknown'}</span>
                        </div>
                        <span className="text-white font-bold">{device.count}</span>
                      </div>
                    );
                  }) || <p className="text-zinc-500 text-sm">Нет данных</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="space-y-6">
            <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
              <h3 className="text-lg font-bold text-white mb-4">Последние транзакции</h3>
              
              {transactions.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">Нет транзакций</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Дата</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Клиент</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Email</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Контакт</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Тариф</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Сумма</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, i) => (
                        <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="py-3 px-4 text-zinc-400 text-sm">
                            {new Date(tx.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="py-3 px-4 text-white text-sm font-medium">
                            {tx.first_name} {tx.last_name}
                          </td>
                          <td className="py-3 px-4 text-zinc-300 text-sm">{tx.email}</td>
                          <td className="py-3 px-4 text-zinc-300 text-sm">{tx.contact}</td>
                          <td className="py-3 px-4 text-zinc-300 text-sm">{tx.package_name}</td>
                          <td className="py-3 px-4 text-amber-400 text-sm font-bold">${tx.amount}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              tx.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                              tx.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {tx.status === 'paid' ? 'Оплачено' : tx.status === 'pending' ? 'Ожидает' : 'Отменено'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Visitors Tab */}
        {activeTab === 'visitors' && (
          <div className="space-y-6">
            <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-400" />
                Активные посетители
              </h3>
              
              {visitors.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">Нет посетителей</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Статус</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Источник</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Устройство</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">IP</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Страница</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Время на сайте</th>
                        <th className="text-left text-zinc-400 text-xs font-medium py-3 px-4">Последняя активность</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visitors.map((v, i) => {
                        const DeviceIcon = v.device_type === 'mobile' ? Smartphone : v.device_type === 'tablet' ? Tablet : Monitor;
                        return (
                          <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                            <td className="py-3 px-4">
                              <div className={`w-3 h-3 rounded-full ${v.is_active ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-600'}`} />
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                v.utm_source === 'instagram' ? 'bg-pink-500/20 text-pink-400' :
                                v.utm_source === 'youtube' ? 'bg-red-500/20 text-red-400' :
                                v.utm_source === 'telegram' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-zinc-700 text-zinc-400'
                              }`}>
                                {v.utm_source || 'Прямой'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <DeviceIcon className="w-4 h-4 text-zinc-400" />
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="text-zinc-400 text-sm font-mono">{v.ip_address}</span>
                                {v.country_code && v.country_code !== '??' && (
                                  <span className="px-2 py-0.5 rounded bg-zinc-700 text-xs text-zinc-300" title={v.city || v.country}>
                                    {v.country_code}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-zinc-300 text-sm">{v.current_page}</td>
                            <td className="py-3 px-4 text-zinc-300 text-sm">{v.total_time_seconds}с</td>
                            <td className="py-3 px-4 text-zinc-400 text-sm">
                              {new Date(v.last_seen).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Flow Date */}
            <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Дата потока</h3>
                  <p className="text-zinc-500 text-sm">Когда начинается следующий поток</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">Дата</label>
                  <input
                    type="date"
                    value={settings?.flow_date || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, flow_date: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">Время</label>
                  <input
                    type="time"
                    value={settings?.flow_time || '19:00'}
                    onChange={(e) => setSettings(prev => ({ ...prev, flow_time: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
              
              <button onClick={handleSaveFlowDate} className="px-6 py-3 rounded-xl bg-blue-500 text-white font-bold hover:bg-blue-400 transition-colors">
                Сохранить дату
              </button>
            </div>

            {/* Per-tier discounts (NEW) */}
            <div className="p-6 rounded-2xl bg-zinc-900 border border-emerald-500/30">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Percent className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Скидки по тарифам</h3>
                    <p className="text-zinc-500 text-sm">Отдельная настройка для каждого тарифа. Видна на <code className="text-amber-400">/discount</code> и <code className="text-amber-400">/faster_discount</code>. «Поток» — везде.</p>
                  </div>
                </div>
                <button
                  onClick={handleResetPromoSessions}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-white text-sm transition-colors"
                  data-testid="reset-promo-sessions-button"
                >
                  <RotateCcw className="w-4 h-4" />
                  Сбросить таймеры посетителей
                </button>
              </div>

              <div className="space-y-4">
                {[
                  { id: 'base',     name: 'Обучаюсь сам', color: 'amber',   note: 'Виден только на /discount или /faster_discount' },
                  { id: 'flow',     name: 'Поток',        color: 'blue',    note: 'Виден на всех страницах' },
                  { id: 'advanced', name: 'Продвинутый',  color: 'violet',  note: 'Виден только на /discount или /faster_discount' },
                  { id: 'pro',      name: 'Про уровень',  color: 'emerald', note: 'Виден только на /discount или /faster_discount' },
                ].map((t) => {
                  const enKey = `${t.id}_discount_enabled`;
                  const tyKey = `${t.id}_discount_type`;
                  const vaKey = `${t.id}_discount_value`;
                  const enabled = !!settings?.[enKey];
                  const dtype = settings?.[tyKey] || 'percent';
                  const dvalue = settings?.[vaKey] || 0;
                  const ringColor = {
                    amber: 'border-amber-500/30',
                    blue: 'border-blue-500/30',
                    violet: 'border-violet-500/30',
                    emerald: 'border-emerald-500/30',
                  }[t.color];
                  const accent = {
                    amber: 'text-amber-400 bg-amber-500/20 border-amber-500/50',
                    blue: 'text-blue-400 bg-blue-500/20 border-blue-500/50',
                    violet: 'text-violet-400 bg-violet-500/20 border-violet-500/50',
                    emerald: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/50',
                  }[t.color];

                  return (
                    <div key={t.id} className={`p-4 rounded-xl bg-zinc-800/40 border ${ringColor}`} data-testid={`tier-discount-${t.id}`}>
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div>
                          <h4 className="text-white font-bold">{t.name}</h4>
                          <p className="text-zinc-500 text-xs">{t.note}</p>
                        </div>
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, [enKey]: !prev?.[enKey] }))}
                          className={`relative w-12 h-7 rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-zinc-700'}`}
                          data-testid={`tier-toggle-${t.id}`}
                        >
                          <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${enabled ? 'left-6' : 'left-1'}`} />
                        </button>
                      </div>

                      {enabled && (
                        <>
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <button
                              onClick={() => setSettings(prev => ({ ...prev, [tyKey]: 'percent' }))}
                              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-sm ${dtype === 'percent' ? accent : 'bg-zinc-900 border-zinc-700 text-zinc-400'}`}
                            >
                              <Percent className="w-3.5 h-3.5" /> Процент
                            </button>
                            <button
                              onClick={() => setSettings(prev => ({ ...prev, [tyKey]: 'fixed' }))}
                              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-sm ${dtype === 'fixed' ? accent : 'bg-zinc-900 border-zinc-700 text-zinc-400'}`}
                            >
                              <DollarSign className="w-3.5 h-3.5" /> Фикс
                            </button>
                          </div>

                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              value={dvalue}
                              onChange={(e) => setSettings(prev => ({ ...prev, [vaKey]: parseFloat(e.target.value) || 0 }))}
                              className="flex-1 px-4 py-2.5 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-lg font-bold focus:border-emerald-500 focus:outline-none"
                              data-testid={`tier-value-${t.id}`}
                            />
                            <span className="text-zinc-500 text-sm">{dtype === 'percent' ? '%' : 'USD'}</span>
                            <button
                              onClick={() => handleSaveTierDiscount(t.id)}
                              className="px-4 py-2.5 rounded-lg bg-emerald-500 text-white font-bold hover:bg-emerald-400 transition-colors text-sm"
                              data-testid={`tier-save-${t.id}`}
                            >
                              Сохранить
                            </button>
                          </div>
                        </>
                      )}

                      {!enabled && (
                        <button
                          onClick={() => handleSaveTierDiscount(t.id)}
                          className="px-4 py-2 rounded-lg bg-zinc-700 text-zinc-300 hover:bg-zinc-600 text-sm transition-colors"
                          data-testid={`tier-save-disabled-${t.id}`}
                        >
                          Сохранить «без скидки»
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50">
                <p className="text-zinc-400 text-xs leading-relaxed">
                  <strong className="text-white">Как работает таймер:</strong> когда посетитель открывает страницу <code className="text-amber-400">/discount</code>, сервер создаёт ему сессию на 24 часа (привязка по cookie + IP + device fingerprint). Если он зайдёт повторно — увидит остаток своего таймера. Через 24 часа таймер сбрасывается, и скидка снова доступна.<br/>
                  <strong className="text-white">Подписанная ссылка из бота:</strong> <code className="text-amber-400">/discount?uid=&hellip;&amp;deadline=&hellip;&amp;sig=&hellip;</code> — сервер проверяет HMAC-подпись (секрет в <code>PROMO_SECRET</code>). Подделать время в URL нельзя.
                </p>
              </div>
            </div>

            {/* Prices */}
            <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Цены тарифов</h3>
                  <p className="text-zinc-500 text-sm">Редактирование цен на тарифы</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {[
                  { id: 'booking', name: 'Бронь места в Поток', default: 80 },
                  { id: 'base', name: 'Обучаюсь сам', default: 700 },
                  { id: 'flow', name: 'Поток', default: 830 },
                  { id: 'advanced', name: 'Продвинутый', default: 1500 },
                  { id: 'pro', name: 'Про уровень', default: 5800 },
                  { id: 'ungating', name: 'Ангейтинг', default: 400 },
                  { id: 'supplier-invoice', name: 'Поставщик и инвойс', default: 200 },
                  { id: 'sourcing-pack', name: 'Sourcing Pack', default: 600 },
                  { id: 'asin-checker', name: 'ASIN Checker', default: 350 },
                  { id: 'repricer', name: 'Репрайсер', default: 200 },
                ].map(pkg => {
                  // Get current price from settings or use default
                  const currentPrice = settings?.prices?.[pkg.id] !== undefined ? settings.prices[pkg.id] : pkg.default;
                  
                  return (
                    <div key={pkg.id} className="flex items-center gap-4 p-4 rounded-xl bg-zinc-800/50">
                      <div className="flex-1">
                        <p className="text-white font-medium">{pkg.name}</p>
                        <p className="text-zinc-500 text-sm">По умолчанию: ${pkg.default}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-400">$</span>
                        <input
                          type="number"
                          value={currentPrice}
                          onChange={(e) => {
                            const newPrice = parseFloat(e.target.value) || 0;
                            setSettings(prev => ({
                              ...prev,
                              prices: { ...(prev.prices || {}), [pkg.id]: newPrice }
                            }));
                          }}
                          className="w-24 px-3 py-2 rounded-lg bg-zinc-700 border border-zinc-600 text-white text-right focus:border-amber-500 focus:outline-none"
                        />
                        <button
                          onClick={() => handleSavePrice(pkg.id, currentPrice)}
                          className="p-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {saved && (
              <div className="fixed bottom-4 right-4 px-6 py-3 rounded-xl bg-emerald-500 text-white font-bold flex items-center gap-2">
                <Check className="w-5 h-5" />
                Сохранено!
              </div>
            )}
          </div>
        )}

        {/* Short Links Tab */}
        {activeTab === 'shortlinks' && (
          <ShortLinksManager 
            shortLinks={shortLinks} 
            setShortLinks={setShortLinks}
            copyToClipboard={copyToClipboard} 
            copied={copied} 
            fetchData={fetchData}
          />
        )}

        {/* Catalog Prices Tab */}
        {activeTab === 'catalog-prices' && (
          <CatalogPricesManager 
            prices={catalogPrices} 
            setPrices={setCatalogPrices}
            setSaved={setSaved}
          />
        )}

        {/* UTM Links Tab */}
        {activeTab === 'utm' && (
          <UTMGenerator copyToClipboard={copyToClipboard} copied={copied} />
        )}

        {/* Check-lead Funnel Tab */}
        {activeTab === 'check-lead' && (
          <CheckLeadStats />
        )}

      </main>
    </div>
  );
};

// =================== CHECK-LEAD STATS ===================
const SOURCE_LABELS = {
  tg:    { name: 'Telegram',  color: 'text-cyan-300',   bg: 'bg-cyan-500/10 border-cyan-500/30' },
  inst:  { name: 'Instagram', color: 'text-pink-300',   bg: 'bg-pink-500/10 border-pink-500/30' },
  you:   { name: 'YouTube',   color: 'text-red-300',    bg: 'bg-red-500/10 border-red-500/30' },
  tik:   { name: 'TikTok',    color: 'text-zinc-200',   bg: 'bg-zinc-500/10 border-zinc-500/40' },
  fb:    { name: 'Facebook',  color: 'text-blue-300',   bg: 'bg-blue-500/10 border-blue-500/30' },
  direct:{ name: 'Прямой',    color: 'text-zinc-400',   bg: 'bg-zinc-800 border-zinc-700' },
};

const CheckLeadStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/check-lead/stats?limit=200`);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const d = await res.json();
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-zinc-400 p-8 text-center">Загружаем…</div>;
  if (error)   return <div className="text-red-400 p-8 text-center">Ошибка: {error}</div>;
  if (!data)   return null;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="check-lead-stats">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-white">Воронка `/check_lead`</h2>
          <p className="text-zinc-500 text-sm mt-1">Кто пришёл, откуда, и кто нажал на кнопку Telegram</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-sm">
          <RefreshCw className="w-4 h-4" /> Обновить
        </button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="p-4 sm:p-5 rounded-xl bg-zinc-900 border border-zinc-800">
          <p className="text-zinc-500 text-xs uppercase tracking-wider font-bold">Всего визитов</p>
          <p className="text-3xl sm:text-4xl font-black text-white mt-1 tabular-nums">{data.total}</p>
        </div>
        <div className="p-4 sm:p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
          <p className="text-emerald-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5"><MousePointerClick className="w-3.5 h-3.5" /> Кликов на бот</p>
          <p className="text-3xl sm:text-4xl font-black text-emerald-300 mt-1 tabular-nums">{data.clicked}</p>
        </div>
        <div className="p-4 sm:p-5 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <p className="text-amber-300 text-xs uppercase tracking-wider font-bold">Конверсия</p>
          <p className="text-3xl sm:text-4xl font-black text-amber-300 mt-1 tabular-nums">{data.click_rate_pct}%</p>
        </div>
      </div>

      {/* By source */}
      <div className="p-4 sm:p-5 rounded-xl bg-zinc-900 border border-zinc-800">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Filter className="w-4 h-4 text-zinc-500" /> По источникам</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 text-xs uppercase tracking-wider text-left">
                <th className="pb-3 pr-4 font-bold">Источник</th>
                <th className="pb-3 pr-4 font-bold tabular-nums">Визитов</th>
                <th className="pb-3 pr-4 font-bold tabular-nums">Кликов</th>
                <th className="pb-3 font-bold tabular-nums">Конверсия</th>
              </tr>
            </thead>
            <tbody className="text-zinc-200">
              {data.by_source.length === 0 && (
                <tr><td colSpan={4} className="py-4 text-zinc-500 text-center">Пока данных нет</td></tr>
              )}
              {data.by_source.map((s) => {
                const lbl = SOURCE_LABELS[s.source] || SOURCE_LABELS.direct;
                return (
                  <tr key={s.source} className="border-t border-zinc-800">
                    <td className="py-3 pr-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${lbl.bg} ${lbl.color}`}>
                        {lbl.name}
                      </span>
                    </td>
                    <td className="py-3 pr-4 tabular-nums">{s.visits}</td>
                    <td className="py-3 pr-4 tabular-nums">{s.clicks}</td>
                    <td className="py-3 tabular-nums">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white min-w-[3.5rem]">{s.rate_pct}%</span>
                        <div className="flex-1 max-w-[120px] h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-400" style={{ width: `${Math.min(100, s.rate_pct)}%` }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent visits */}
      <div className="p-4 sm:p-5 rounded-xl bg-zinc-900 border border-zinc-800">
        <h3 className="text-white font-bold mb-4">Последние визиты ({data.recent_visits.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 text-xs uppercase tracking-wider text-left">
                <th className="pb-3 pr-3 font-bold">Время</th>
                <th className="pb-3 pr-3 font-bold">Источник</th>
                <th className="pb-3 pr-3 font-bold">Нажал?</th>
                <th className="pb-3 pr-3 font-bold">IP</th>
                <th className="pb-3 font-bold hidden md:table-cell">User-Agent</th>
              </tr>
            </thead>
            <tbody className="text-zinc-200">
              {data.recent_visits.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-zinc-500 text-center">Пока визитов нет</td></tr>
              )}
              {data.recent_visits.map((v) => {
                const lbl = SOURCE_LABELS[v.source] || SOURCE_LABELS.direct;
                return (
                  <tr key={v.visit_id} className="border-t border-zinc-800">
                    <td className="py-2.5 pr-3 text-zinc-400 whitespace-nowrap">{fmtDate(v.created_at)}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${lbl.bg} ${lbl.color}`}>
                        {lbl.name}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      {v.clicked ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400 font-bold">
                          <Check className="w-3.5 h-3.5" /> Да
                        </span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 text-zinc-500 font-mono text-xs">{v.ip}</td>
                    <td className="py-2.5 text-zinc-600 text-xs truncate max-w-[200px] hidden md:table-cell">{v.user_agent}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Catalog Prices Manager Component
const CatalogPricesManager = ({ prices, setPrices, setSaved }) => {
  const [localPrices, setLocalPrices] = useState({});
  const [saving, setSaving] = useState(false);

  // Product definitions
  const products = [
    { id: 'critical_mistakes', name: 'Критические ошибки', hasOldPrice: true, color: 'rose' },
    { id: 'ungating', name: 'Ангейтинг', color: 'amber' },
    { id: 'supplier_invoice', name: 'Поставщик и инвойс', color: 'blue' },
    { id: 'sourcing_pack', name: 'Sourcing Pack', color: 'emerald' },
    { id: 'asin_checker', name: 'ASIN Checker', color: 'cyan' },
    { id: 'repricer', name: 'Репрайсер', color: 'violet' },
    { id: 'website', name: 'Разработка сайта', isText: true, color: 'indigo' },
  ];

  useEffect(() => {
    if (prices) {
      setLocalPrices(prices);
    }
  }, [prices]);

  const handlePriceChange = (productId, value, field = 'price') => {
    const key = field === 'old' ? `${productId}_old` : field === 'text' ? `${productId}_text` : productId;
    setLocalPrices(prev => ({
      ...prev,
      [key]: field === 'text' ? value : (value === '' ? null : Number(value))
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 sec timeout
      
      const res = await fetch(`${API_URL}/api/admin/catalog/prices`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(localPrices),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (res.ok) {
        setPrices(localPrices);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        const errorText = await res.text();
        alert('Ошибка сохранения: ' + errorText);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        alert('Превышено время ожидания (30 сек). Проверьте подключение к базе данных.');
      } else {
        console.error('Error saving prices:', error);
        alert('Ошибка сети: ' + error.message);
      }
    }
    setSaving(false);
  };

  const colorStyles = {
    rose: 'from-rose-500/20 to-rose-600/20 border-rose-500/30',
    amber: 'from-amber-500/20 to-amber-600/20 border-amber-500/30',
    blue: 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
    emerald: 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30',
    cyan: 'from-cyan-500/20 to-cyan-600/20 border-cyan-500/30',
    violet: 'from-violet-500/20 to-violet-600/20 border-violet-500/30',
    indigo: 'from-indigo-500/20 to-indigo-600/20 border-indigo-500/30',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Tag className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Цены каталога</h2>
            <p className="text-zinc-500 text-sm">Управление ценами всех продуктов</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold hover:shadow-lg transition-all disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Сохранение...' : 'Сохранить все'}
          </button>
          
          <button
            onClick={async () => {
              if (window.confirm('Сбросить все цены к значениям по умолчанию?')) {
                try {
                  const res = await fetch(`${API_URL}/api/admin/catalog/prices/reset`, { method: 'POST' });
                  if (res.ok) {
                    const data = await res.json();
                    setLocalPrices(data.prices);
                    setPrices(data.prices);
                    setSaved(true);
                    setTimeout(() => setSaved(false), 2000);
                  }
                } catch (error) {
                  console.error('Error resetting prices:', error);
                }
              }
            }}
            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-zinc-700 text-zinc-300 font-medium hover:bg-zinc-600 transition-all"
          >
            <RotateCcw className="w-4 h-4" />
            Сбросить
          </button>
          
          <button
            onClick={async () => {
              try {
                // First check DB health
                const healthRes = await fetch(`${API_URL}/api/admin/db-health`, {
                  signal: AbortSignal.timeout(15000)
                });
                
                if (!healthRes.ok) {
                  const errText = await healthRes.text();
                  alert('База данных недоступна: ' + errText);
                  return;
                }
                
                const health = await healthRes.json();
                console.log('DB Health:', health);
                
                if (health.status === 'error') {
                  alert('Ошибка БД: ' + health.error);
                  return;
                }
                
                alert('БД доступна: ' + JSON.stringify(health));
                
                // Then init
                const res = await fetch(`${API_URL}/api/admin/init-db`, { 
                  method: 'POST',
                  signal: AbortSignal.timeout(30000)
                });
                
                const responseText = await res.text();
                console.log('Init response:', res.status, responseText);
                
                if (res.ok) {
                  try {
                    const data = JSON.parse(responseText);
                    setLocalPrices(data.prices);
                    setPrices(data.prices);
                    alert('База данных инициализирована!');
                    window.location.reload();
                  } catch (e) {
                    alert('Ответ: ' + responseText);
                  }
                } else {
                  alert('Ошибка инициализации (' + res.status + '): ' + responseText);
                }
              } catch (error) {
                if (error.name === 'TimeoutError' || error.name === 'AbortError') {
                  alert('Таймаут подключения к БД.');
                } else {
                  alert('Ошибка: ' + error.message);
                }
              }
            }}
            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500 transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Инит БД
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
        <p className="text-blue-300 text-sm">
          💡 Изменения цен применяются на всём сайте: каталог, страницы продуктов, секция тарифов.
        </p>
      </div>

      {/* Products Grid */}
      <div className="grid gap-4">
        {products.map((product) => (
          <div 
            key={product.id}
            className={`p-5 rounded-xl bg-gradient-to-r ${colorStyles[product.color]} border`}
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <h3 className="text-lg font-bold text-white mb-1">{product.name}</h3>
                <p className="text-zinc-400 text-sm">ID: {product.id}</p>
              </div>
              
              <div className="flex items-center gap-4 flex-wrap">
                {product.isText ? (
                  <div>
                    <label className="block text-zinc-400 text-xs mb-1">Текст вместо цены</label>
                    <input
                      type="text"
                      value={localPrices[`${product.id}_text`] || ''}
                      onChange={(e) => handlePriceChange(product.id, e.target.value, 'text')}
                      placeholder="Обсудить проект"
                      className="w-48 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm"
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-zinc-400 text-xs mb-1">Цена ($)</label>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-500">$</span>
                        <input
                          type="number"
                          value={localPrices[product.id] ?? ''}
                          onChange={(e) => handlePriceChange(product.id, e.target.value)}
                          placeholder="0"
                          className="w-24 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-lg font-bold"
                        />
                      </div>
                    </div>
                    
                    {product.hasOldPrice && (
                      <div>
                        <label className="block text-zinc-400 text-xs mb-1">Старая цена ($)</label>
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-500">$</span>
                          <input
                            type="number"
                            value={localPrices[`${product.id}_old`] ?? ''}
                            onChange={(e) => handlePriceChange(product.id, e.target.value, 'old')}
                            placeholder="0"
                            className="w-24 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-400 line-through"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Short Links Manager Component
const ShortLinksManager = ({ shortLinks, setShortLinks, copyToClipboard, copied, fetchData }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    original_url: '',
    custom_code: '',
    title: ''
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [selectedStats, setSelectedStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const resetForm = () => {
    setFormData({ original_url: '', custom_code: '', title: '' });
    setEditingLink(null);
    setShowForm(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    
    try {
      const url = editingLink 
        ? `${API_URL}/api/admin/shortlinks/${editingLink.id}`
        : `${API_URL}/api/admin/shortlinks`;
      
      const res = await fetch(url, {
        method: editingLink ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Ошибка при создании ссылки');
      }
      
      await fetchData();
      resetForm();
    } catch (err) {
      setError(err.message);
    }
    
    setCreating(false);
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Удалить эту ссылку?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/shortlinks/${linkId}`, {
        method: 'DELETE'
      });
      setShortLinks(prev => prev.filter(l => l.id !== linkId));
    } catch (err) {
      console.error('Error deleting link:', err);
    }
  };

  const handleEdit = (link) => {
    setFormData({
      original_url: link.original_url,
      custom_code: link.code,
      title: link.title || ''
    });
    setEditingLink(link);
    setShowForm(true);
  };

  const loadStats = async (linkId) => {
    setStatsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/shortlinks/${linkId}/stats`);
      if (res.ok) {
        const data = await res.json();
        setSelectedStats(data);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
    setStatsLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <LinkIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Короткие ссылки</h2>
            <p className="text-zinc-500 text-sm">Создавайте и отслеживайте короткие URL</p>
          </div>
        </div>
        
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white font-bold hover:shadow-lg hover:shadow-violet-500/30 transition-all"
        >
          <Plus className="w-5 h-5" />
          Создать ссылку
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="p-6 rounded-2xl bg-zinc-900 border border-violet-500/30">
          <h3 className="text-lg font-bold text-white mb-4">
            {editingLink ? 'Редактировать ссылку' : 'Новая короткая ссылка'}
          </h3>
          
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-zinc-400 text-sm mb-2">Оригинальная ссылка *</label>
              <input
                type="url"
                value={formData.original_url}
                onChange={(e) => setFormData(prev => ({ ...prev, original_url: e.target.value }))}
                placeholder="https://example.com/very-long-url..."
                required
                className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none font-mono text-sm"
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Кастомный код (опционально)</label>
                <div className="flex items-center">
                  <span className="px-3 py-3 bg-zinc-700 border border-zinc-600 border-r-0 rounded-l-xl text-zinc-400 text-sm">
                    /s/
                  </span>
                  <input
                    type="text"
                    value={formData.custom_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_code: e.target.value.replace(/[^a-zA-Z0-9-_]/g, '') }))}
                    placeholder="my-link"
                    className="flex-1 px-4 py-3 rounded-r-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none"
                  />
                </div>
                <p className="text-zinc-600 text-xs mt-1">Оставьте пустым для автогенерации</p>
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Название</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Instagram Bio Link"
                  className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={creating}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white font-bold hover:shadow-lg transition-all disabled:opacity-50"
              >
                {creating ? 'Сохранение...' : (editingLink ? 'Сохранить' : 'Создать')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 rounded-xl bg-zinc-800 text-zinc-300 font-bold hover:bg-zinc-700 transition-colors"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stats Modal */}
      {selectedStats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedStats(null)}>
          <div className="relative w-full max-w-lg bg-zinc-900 rounded-2xl border border-zinc-800 p-6" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setSelectedStats(null)}
              className="absolute top-4 right-4 p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-lg font-bold text-white mb-4">Статистика кликов</h3>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/30 text-center">
                <p className="text-2xl font-black text-violet-400">{selectedStats.total_clicks}</p>
                <p className="text-zinc-500 text-xs">Всего кликов</p>
              </div>
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 text-center">
                <p className="text-2xl font-black text-blue-400">{Object.keys(selectedStats.countries || {}).length}</p>
                <p className="text-zinc-500 text-xs">Стран</p>
              </div>
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                <p className="text-2xl font-black text-emerald-400">{Object.keys(selectedStats.devices || {}).length}</p>
                <p className="text-zinc-500 text-xs">Типов устройств</p>
              </div>
            </div>
            
            {/* Devices */}
            {Object.keys(selectedStats.devices || {}).length > 0 && (
              <div className="mb-4">
                <p className="text-zinc-400 text-sm mb-2">Устройства:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(selectedStats.devices).map(([device, count]) => (
                    <span key={device} className="px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 text-sm">
                      {device}: <span className="text-white font-bold">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Sources */}
            {Object.keys(selectedStats.sources || {}).length > 0 && (
              <div>
                <p className="text-zinc-400 text-sm mb-2">Источники:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(selectedStats.sources).map(([source, count]) => (
                    <span key={source} className="px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 text-sm">
                      {source}: <span className="text-white font-bold">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Links List */}
      <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
        <h3 className="text-lg font-bold text-white mb-4">Все ссылки ({shortLinks.length})</h3>
        
        {shortLinks.length === 0 ? (
          <div className="text-center py-12">
            <LinkIcon className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">Нет созданных ссылок</p>
            <p className="text-zinc-600 text-sm">Создайте первую короткую ссылку</p>
          </div>
        ) : (
          <div className="space-y-3">
            {shortLinks.map((link) => (
              <div key={link.id} className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50 hover:border-violet-500/30 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-medium truncate">{link.title || 'Без названия'}</span>
                      <span className="px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400 text-xs font-medium">
                        {link.clicks} кликов
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <code className="text-sm text-violet-400 font-mono">{link.short_url}</code>
                      <button
                        onClick={() => copyToClipboard(link.short_url, link.id)}
                        className={`p-1 rounded transition-colors ${
                          copied === link.id ? 'text-emerald-400' : 'text-zinc-500 hover:text-white'
                        }`}
                      >
                        {copied === link.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    
                    <p className="text-zinc-500 text-xs truncate">{link.original_url}</p>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => loadStats(link.id)}
                      className="p-2 rounded-lg bg-zinc-700/50 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                      title="Статистика"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(link)}
                      className="p-2 rounded-lg bg-zinc-700/50 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                      title="Редактировать"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(link.id)}
                      className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <a
                      href={link.original_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-zinc-700/50 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                      title="Открыть оригинал"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 mt-2 pt-2 border-t border-zinc-700/50 text-xs text-zinc-500">
                  <span>Создана: {new Date(link.created_at).toLocaleDateString('ru-RU')}</span>
                  {link.last_clicked && (
                    <span>Последний клик: {new Date(link.last_clicked).toLocaleDateString('ru-RU')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Custom UTM Generator Component
const UTMGenerator = ({ copyToClipboard, copied }) => {
  const [utmParams, setUtmParams] = useState({
    base_url: 'https://vadjik.com',
    page: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
  });
  
  const [savedLinks, setSavedLinks] = useState(() => {
    try {
      const saved = localStorage.getItem('custom_utm_links');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const generateUrl = () => {
    let url = utmParams.base_url;
    if (utmParams.page) {
      url += '/' + utmParams.page.replace(/^\//, '');
    }
    
    const params = new URLSearchParams();
    if (utmParams.utm_source) params.append('utm_source', utmParams.utm_source);
    if (utmParams.utm_medium) params.append('utm_medium', utmParams.utm_medium);
    if (utmParams.utm_campaign) params.append('utm_campaign', utmParams.utm_campaign);
    if (utmParams.utm_content) params.append('utm_content', utmParams.utm_content);
    if (utmParams.utm_term) params.append('utm_term', utmParams.utm_term);
    
    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
  };

  const saveLink = () => {
    const url = generateUrl();
    const newLink = {
      id: Date.now(),
      name: `${utmParams.utm_source || 'custom'} - ${utmParams.utm_campaign || 'link'}`,
      url,
      params: { ...utmParams },
      created: new Date().toISOString(),
    };
    
    const updated = [newLink, ...savedLinks].slice(0, 20); // Keep max 20 links
    setSavedLinks(updated);
    localStorage.setItem('custom_utm_links', JSON.stringify(updated));
  };

  const deleteLink = (id) => {
    const updated = savedLinks.filter(l => l.id !== id);
    setSavedLinks(updated);
    localStorage.setItem('custom_utm_links', JSON.stringify(updated));
  };

  const generatedUrl = generateUrl();

  return (
    <div className="space-y-6">
      {/* UTM Generator */}
      <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            <Link2 className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Генератор UTM ссылок</h3>
            <p className="text-zinc-500 text-sm">Создайте свои UTM-метки для отслеживания трафика</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Base URL */}
          <div className="md:col-span-2">
            <label className="block text-zinc-400 text-sm mb-2">Базовый домен</label>
            <input
              type="text"
              value={utmParams.base_url}
              onChange={(e) => setUtmParams(prev => ({ ...prev, base_url: e.target.value }))}
              placeholder="https://vadjik.com"
              className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none font-mono"
            />
          </div>
          
          {/* Page */}
          <div>
            <label className="block text-zinc-400 text-sm mb-2">Страница (опционально)</label>
            <select
              value={utmParams.page}
              onChange={(e) => setUtmParams(prev => ({ ...prev, page: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white focus:border-violet-500 focus:outline-none"
            >
              <option value="">Главная страница</option>
              <option value="catalog">Каталог</option>
              <option value="faster_discount">Страница со скидкой</option>
              <option value="safety">Страница чек-листов</option>
            </select>
          </div>
          
          {/* UTM Source */}
          <div>
            <label className="block text-zinc-400 text-sm mb-2">utm_source <span className="text-violet-400">*</span></label>
            <input
              type="text"
              value={utmParams.utm_source}
              onChange={(e) => setUtmParams(prev => ({ ...prev, utm_source: e.target.value }))}
              placeholder="instagram, youtube, telegram, facebook..."
              className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none"
            />
          </div>
          
          {/* UTM Medium */}
          <div>
            <label className="block text-zinc-400 text-sm mb-2">utm_medium</label>
            <input
              type="text"
              value={utmParams.utm_medium}
              onChange={(e) => setUtmParams(prev => ({ ...prev, utm_medium: e.target.value }))}
              placeholder="social, video, bio, story, post..."
              className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none"
            />
          </div>
          
          {/* UTM Campaign */}
          <div>
            <label className="block text-zinc-400 text-sm mb-2">utm_campaign</label>
            <input
              type="text"
              value={utmParams.utm_campaign}
              onChange={(e) => setUtmParams(prev => ({ ...prev, utm_campaign: e.target.value }))}
              placeholder="course_launch, promo_jan, sale..."
              className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none"
            />
          </div>
          
          {/* UTM Content */}
          <div>
            <label className="block text-zinc-400 text-sm mb-2">utm_content (опционально)</label>
            <input
              type="text"
              value={utmParams.utm_content}
              onChange={(e) => setUtmParams(prev => ({ ...prev, utm_content: e.target.value }))}
              placeholder="banner_top, link_bio, button_cta..."
              className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none"
            />
          </div>
          
          {/* UTM Term */}
          <div>
            <label className="block text-zinc-400 text-sm mb-2">utm_term (опционально)</label>
            <input
              type="text"
              value={utmParams.utm_term}
              onChange={(e) => setUtmParams(prev => ({ ...prev, utm_term: e.target.value }))}
              placeholder="amazon_course, wholesale..."
              className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none"
            />
          </div>
        </div>
        
        {/* Generated URL Preview */}
        <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50 mb-4">
          <p className="text-zinc-500 text-xs mb-2">Сгенерированная ссылка:</p>
          <code className="block text-sm text-violet-400 break-all">
            {generatedUrl}
          </code>
        </div>
        
        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => copyToClipboard(generatedUrl, 'generated')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition-all ${
              copied === 'generated' 
                ? 'bg-emerald-500 text-white' 
                : 'bg-violet-500 text-white hover:bg-violet-400'
            }`}
          >
            {copied === 'generated' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            {copied === 'generated' ? 'Скопировано!' : 'Копировать ссылку'}
          </button>
          
          <button
            onClick={saveLink}
            disabled={!utmParams.utm_source}
            className="px-6 py-3 rounded-xl font-bold bg-zinc-700 text-white hover:bg-zinc-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-5 h-5" />
            Сохранить
          </button>
        </div>
      </div>
      
      {/* Quick Templates */}
      <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
        <h3 className="text-lg font-bold text-white mb-4">Быстрые шаблоны</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Instagram Stories', source: 'instagram', medium: 'stories' },
            { label: 'Instagram Post', source: 'instagram', medium: 'post' },
            { label: 'Instagram Bio', source: 'instagram', medium: 'bio' },
            { label: 'YouTube Video', source: 'youtube', medium: 'video' },
            { label: 'YouTube Shorts', source: 'youtube', medium: 'shorts' },
            { label: 'Telegram Channel', source: 'telegram', medium: 'channel' },
            { label: 'Telegram Bot', source: 'telegram', medium: 'bot' },
            { label: 'Facebook', source: 'facebook', medium: 'social' },
            { label: 'TikTok', source: 'tiktok', medium: 'video' },
            { label: 'Email', source: 'email', medium: 'newsletter' },
          ].map((template, i) => (
            <button
              key={i}
              onClick={() => setUtmParams(prev => ({ 
                ...prev, 
                utm_source: template.source, 
                utm_medium: template.medium 
              }))}
              className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors text-sm"
            >
              {template.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Saved Links */}
      {savedLinks.length > 0 && (
        <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
          <h3 className="text-lg font-bold text-white mb-4">Сохранённые ссылки</h3>
          <div className="space-y-3">
            {savedLinks.map((link) => (
              <div key={link.id} className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">{link.name}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyToClipboard(link.url, link.id)}
                      className={`px-3 py-1.5 rounded-lg transition-colors text-sm ${
                        copied === link.id ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                      }`}
                    >
                      {copied === link.id ? 'Скопировано!' : 'Копировать'}
                    </button>
                    <button
                      onClick={() => deleteLink(link.id)}
                      className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <code className="block text-xs text-zinc-400 break-all">
                  {link.url}
                </code>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
